import { Button, JWTRole, Text } from "@hackthenorth/north";
import React, { useEffect, useState } from "react";
import {
  Route as RouterRoute,
  RouteProps as RouterRouteProps,
  useLocation,
  useNavigate,
} from "react-router";
import { Navigate } from "react-router-dom";

import Modal from "src/shared/components/Modal";
import PageWrapper from "src/shared/components/PageWrapper";
import { BaseRoute, Route as RouteName } from "src/shared/constants/route";
import {
  useHackerContext,
  useEventContext,
  useUserContext,
  usePermissionsContext,
} from "src/shared/contexts";
import { HackerStage } from "src/shared/contexts/HackerContext/types";
import { Permission } from "src/shared/contexts/PermissionsContext/types";
import { canAccessHackerPostRsvpTools } from "src/shared/utils/hackerPostRsvpAccess";
import { canAccessHardware } from "src/shared/utils/hardwareAccess";
import { canAccessJudging } from "src/shared/utils/judgingAccess";
import { canAccessTravelSupportBot } from "src/shared/utils/travelSupportAccess";

import AuthenticationLoadingModal from "./AuthenticationLoadingModal";

export interface RouteProps extends RouterRouteProps {
  /**
   * Require users to be authenticated to access this route.
   */
  requireAuth?: boolean;
  /**
   * Require all permissions in requirePermissions for access
   * If false, only require one permission from the array
   * Default: true
   */
  requireAll?: boolean;
  /**
   * Require users to be authenticated and hold valid permissions.
   */
  requirePermissions?: Permission[];
  /** Gate event-time tools from hackers until the shared launch flag is enabled. */
  requireHackerPostRsvpTools?: boolean;
  /** Limit the route to organizers and hackers with the event flag enabled. */
  requireTravelSupportBot?: boolean;
  /** Limit hardware to organizers, allowlisted volunteers, and hackers with eventMeta.hardware.isOpen. */
  requireHardwareOpen?: boolean;
  /** Limit judging to organizers and hackers once eventMeta.judging.isOpen is true. */
  requireJudgingOpen?: boolean;
  permissionDeniedTitle?: string;
  permissionDeniedMessage?: string;
  permissionDeniedActionLabel?: string;
  permissionDeniedRedirectTo?: string;
  permissionDeniedFor?: Permission;
}

const Route: React.FC<RouteProps> = ({
  requireAuth,
  requireAll = true,
  requirePermissions,
  requireHackerPostRsvpTools,
  requireTravelSupportBot,
  requireHardwareOpen,
  requireJudgingOpen,
  permissionDeniedTitle = "Access denied",
  permissionDeniedMessage = "You are not permitted to view this page.",
  permissionDeniedActionLabel = "Go home",
  permissionDeniedRedirectTo = BaseRoute.HOME,
  permissionDeniedFor,
  element,
  ...rest
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isOrganizer, logIn, roles, email } =
    useUserContext();
  const { stage } = useHackerContext();
  const {
    hackerPostRsvpTools,
    travelSupportBot,
    hardware,
    judging,
    isLoading: isEventLoading,
  } = useEventContext();
  const { hasPermissions, isLoading: isPermissionsLoading } =
    usePermissionsContext();
  const isHackerPostRsvpToolsGateApplicable =
    !isOrganizer && roles.includes(JWTRole.HACKER);
  const isLoading =
    isPermissionsLoading ||
    ((requireTravelSupportBot ||
      (requireHackerPostRsvpTools && isHackerPostRsvpToolsGateApplicable)) &&
      isEventLoading);
  const [showLoading, setShowLoading] = useState(true);
  const statusOnlyHacker =
    !isOrganizer &&
    roles.includes(JWTRole.HACKER) &&
    !!stage &&
    [
      HackerStage.WAITLISTED,
      HackerStage.NOT_ACCEPTED,
      HackerStage.WITHDRAWN,
    ].includes(stage);
  const canAccessPostRsvpTools = canAccessHackerPostRsvpTools(
    roles,
    isOrganizer,
    hackerPostRsvpTools.isOpen
  );
  const canAccessTravelSupport = canAccessTravelSupportBot(
    roles,
    isOrganizer,
    travelSupportBot.enabled
  );
  const canAccessHardwareSection = canAccessHardware(
    roles,
    isOrganizer,
    hardware.isOpen,
    email,
    hardware.volunteersWithAccess
  );
  const canAccessJudgingSection = canAccessJudging(
    roles,
    isOrganizer,
    judging.isOpen
  );
  const useCustomPermissionDeniedCopy =
    !permissionDeniedFor || !hasPermissions([permissionDeniedFor]);

  // Only allow loading modal to show up once on initial render
  useEffect(() => {
    if (!isLoading && showLoading) {
      setShowLoading(false);
    }
  }, [isLoading, showLoading]);

  let internalElement = element;
  if (showLoading) {
    internalElement = <AuthenticationLoadingModal />;
  }
  // Restrict "Access Denied" modals to only display after all dependencies have been loaded
  else if (
    (requireAuth || requirePermissions) &&
    !isAuthenticated &&
    !isLoading
  ) {
    internalElement = (
      <>
        <Modal
          isOpen
          title="Access denied"
          actions={
            <Button mods="primary" onClick={logIn}>
              Log in
            </Button>
          }
        >
          <Text>You are not logged in!</Text>
        </Modal>
        <PageWrapper pageTitle="" />
      </>
    );
  } else if (
    statusOnlyHacker &&
    ![
      RouteName.HACKER_RSVP,
      RouteName.HACKER_TRAVEL_SUPPORT,
      RouteName.BADGE_MY_BADGE,
    ].includes(location.pathname)
  ) {
    internalElement = <Navigate to={RouteName.HACKER_RSVP} replace />;
  } else if (
    requirePermissions?.includes(Permission.RSVP_EDIT) &&
    !hasPermissions([Permission.RSVP_EDIT]) &&
    !isLoading
  ) {
    internalElement = <Navigate to={RouteName.HACKER_RSVP} replace />;
  } else if (
    (!hasPermissions(requirePermissions, requireAll) ||
      (requireHackerPostRsvpTools && !canAccessPostRsvpTools) ||
      (requireTravelSupportBot && !canAccessTravelSupport) ||
      (requireHardwareOpen && !canAccessHardwareSection) ||
      (requireJudgingOpen && !canAccessJudgingSection)) &&
    !isLoading
  ) {
    internalElement = (
      <>
        <Modal
          isOpen
          title={
            useCustomPermissionDeniedCopy
              ? permissionDeniedTitle
              : "Access denied"
          }
          actions={
            <Button
              mods="primary"
              onClick={() =>
                navigate(
                  useCustomPermissionDeniedCopy
                    ? permissionDeniedRedirectTo
                    : BaseRoute.HOME
                )
              }
            >
              {useCustomPermissionDeniedCopy
                ? permissionDeniedActionLabel
                : "Go home"}
            </Button>
          }
        >
          <Text>
            {useCustomPermissionDeniedCopy
              ? permissionDeniedMessage
              : "You are not permitted to view this page."}
          </Text>
        </Modal>
        <PageWrapper pageTitle="" />
      </>
    );
  }

  return <RouterRoute {...rest} element={internalElement} />;
};

export default Route;
